import { useState, useEffect } from 'react';
import { Card, Col, Row, Input, Tag, Pagination } from 'antd';
import Header from '../../components/Header';
import { Link, useSearchParams } from 'react-router-dom';
import { useGetBallooningProjectsQuery } from '../../redux/api/ballooning';
import dayjs from 'dayjs';
import { BallooningProject } from '../../interfaces/Ballooning';
import { getTagColor } from '../../utils/index.ts';

const { Meta } = Card;

const BallooningProjects = () => {
	// Initialize search params from the URL
	const [searchParams, setSearchParams] = useSearchParams();
	const initialSearchTerm = searchParams.get('search') || '';
	const pageParam = Number(searchParams.get('page')) || 1;

	// Maintain separate state for the input field and the actual search term used for the API call
	const [inputValue, setInputValue] = useState(initialSearchTerm);
	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

	// Update the component state if the URL's search param changes externally.
	useEffect(() => {
		const param = searchParams.get('search') || '';
		if (param !== searchTerm) {
			setSearchTerm(param);
			setInputValue(param);
		}
	}, [searchParams, searchTerm]);

	// The API call uses the searchTerm which is updated only when the search is executed.
	// Paginated (PTB-6878): the unpaginated list asked for every project and
	// OOM-killed the API. The page lives in the URL so back/forward keeps it.
	const { data, isFetching } = useGetBallooningProjectsQuery({ search: searchTerm, page: pageParam });

	// Handle search: update state and URL query parameters.
	const handleSearch = (value: string) => {
		setSearchTerm(value);
		// A new search always starts from page 1.
		if (value) {
			setSearchParams({ search: value });
		} else {
			setSearchParams({});
		}
	};

	const handlePageChange = (page: number) => {
		const next: Record<string, string> = {};
		if (searchTerm) next.search = searchTerm;
		if (page > 1) next.page = String(page);
		setSearchParams(next);
	};

	return (
		<div className="container w-full">
			<Header
				title="Ballooning"
				breadcrumbItems={[
					{ title: <Link to="/"> Home </Link> },
					{ title: <Link to="/ballooning"> Ballooning </Link> },
				]}
			/>

			{/* Search Bar: Executes search on press enter or search icon click */}
			<Input.Search
				placeholder="Search Projects"
				allowClear
				value={inputValue}
				onChange={(e) => setInputValue(e.target.value)}
				onSearch={handleSearch}
				className="mb-5"
				style={{ width: '480px' }}
			/>

			<Row justify="start" gutter={[8, 8]}>
				{data?.results.map((project: BallooningProject) => (
					<Col key={project.id} span={12} xl={4} lg={4} md={6} sm={12}>
						<Link to={`/ballooning/${project.id}`}>
							<Card
								className="h-full flex flex-col justify-between"
								hoverable
								cover={
									<img
										alt="example"
										className="p-2"
										src={project.order_line_item.drawing_file}
									/>
								}
							>
								<Meta
									title={project.order_line_item.name}
									description={
										<div className="flex flex-col gap-1">
											<div>{project.order_line_item.order?.name}</div>

											<div>
												Created:{' '}
												{dayjs(project.created_time).format('MM-DD-YY')}
											</div>

											<div>
												{project.unit === 'MM' ? (
													<Tag color={getTagColor(project.unit)}>
														Millimeter
													</Tag>
												) : project.unit === 'INCH' ? (
													<Tag color={getTagColor(project.unit)}>Inch</Tag>
												) : (
													<Tag>{project.unit}</Tag>
												)}
											</div>
										</div>
									}
								/>
							</Card>
						</Link>
					</Col>
				))}
			</Row>

			{(data?.count ?? 0) > (data?.pageSize ?? 0) && (
				<Pagination
					className="mt-5"
					current={pageParam}
					pageSize={data?.pageSize}
					total={data?.count}
					showSizeChanger={false}
					disabled={isFetching}
					onChange={handlePageChange}
				/>
			)}
		</div>
	);
};

export default BallooningProjects;
